import { ISelectOptionItem } from '@tencent/udesign/dist';
import {
  AUTO_TABLE_CUSTOM_VALUE_ALL,
  isHitApiCacheRule,
  log,
  setCacheApi,
} from '.';
import { ENV_DEV } from '../../../config';
import { IString } from '../../../model';
import {
  FieldOptSource,
  IConfigField,
  IFieldOpt,
  IFieldOptAPI,
  IFieldOptDataRelationKeyValue,
  IFieldOptMock,
  IFieldValueExtensionSelect,
} from '../model';
import { adapterXHR } from './xhr';

const dataConversion = (
  data: ReadonlyArray<any>,
  relationKeyValue?: IFieldOptDataRelationKeyValue
) => {
  if (!relationKeyValue) {
    return data;
  }
  return data.map((item) => {
    const { label, value } = relationKeyValue;
    return Object.assign({}, item, {
      label: item[label],
      value: item[value],
    });
  });
};

// 回写到字段配置中
const configFieldOptDataWriteBack = (
  opt: IFieldOpt,
  data: ReadonlyArray<ISelectOptionItem>
) => {
  const json: IString<ISelectOptionItem> = {};
  data.forEach((item) => {
    json[item.value] = item;
  });
  Object.assign(opt, {
    data,
    jsonData: json,
  });
};

const formatOptMock = (
  fieldOpt: IFieldOptMock
): ReadonlyArray<ISelectOptionItem> => {
  if (fieldOpt.data.length) {
    const { responseConfig } = fieldOpt;
    if (responseConfig) {
      if (responseConfig.transformData) {
        if (typeof responseConfig.transformData === 'string') {
          /* eslint-disable */
          return eval(
            `responseConfig.transformData(${fieldOpt.data}, ${fieldOpt})`
          );
        }
        return responseConfig.transformData(fieldOpt.data, fieldOpt);
      }
      if (responseConfig.relationKeyValue) {
        return dataConversion(fieldOpt.data, responseConfig.relationKeyValue);
      }
    }
    return fieldOpt.data;
  }
  return [];
};

export const formatOptApi = async (
  fieldOpt: IFieldOptAPI,
  cacheFilterValues?: IString<any>
) => {
  const res = await adapterXHR(fieldOpt, cacheFilterValues);
  if (!res) {
    if (ENV_DEV) {
      return fieldOpt.data || [];
    }
    return [];
  }

  const responseConfig = fieldOpt.responseConfig || {};
  if (responseConfig.transformData) {
    if (typeof responseConfig.transformData === 'string') {
      /* eslint-disable */
      try {
        return eval(
          `(${responseConfig.transformData})(${JSON.stringify(res)})`
        );
      } catch (err) {
        log.error(err);
      }
      return [];
    }
    return responseConfig.transformData(res, fieldOpt);
  }
  if (Array.isArray(res)) {
    if (responseConfig.relationKeyValue) {
      return dataConversion(res, responseConfig.relationKeyValue);
    }
    return res;
  }

  log.error(
    `formatOptApi error, res value type not array, res: ${Object.prototype.toString.call(
      res
    )}`
  );
  return [];
};

// 字段请求数据合并
export const mergeFieldConfigOptExtension = async <T>(
  fieldConfig: IConfigField<T>,
  callback: (fieldConfig: IConfigField<T>) => void,
  cache?: boolean, // 是否走cache
  cacheFilterValues?: IString<any>
) => {
  const { fieldOptExtension } = fieldConfig;
  if (fieldOptExtension) {
    // 是否开启 API 缓存
    if (fieldOptExtension.isEnableCache) {
      const isHit = isHitApiCacheRule(fieldConfig);
      if (isHit) {
        return callback(fieldConfig);
      }

      setCacheApi(fieldConfig);
    }

    // 如果在初始化阶段，并且已经有了数据，则不重新请求
    if (cache) {
      if (
        fieldOptExtension?.data?.length &&
        Object.keys(fieldOptExtension.jsonData || {}).length
      ) {
        return callback(fieldConfig);
      }
    }

    let data: ISelectOptionItem[] = [];

    if (fieldOptExtension.kind === FieldOptSource.mock) {
      const tempData = formatOptMock(fieldOptExtension);
      data = tempData.slice();
    }
    if (fieldOptExtension.kind === FieldOptSource.api) {
      const tempData = await formatOptApi(fieldOptExtension, cacheFilterValues);
      data = tempData.slice();
    }

    configFieldOptDataWriteBack(fieldOptExtension, data);
    Object.assign(fieldConfig.fieldOptExtension, {
      data,
    });

    // 数据准备完毕，通知
    if (fieldOptExtension.dataComplete) {
      fieldOptExtension.dataComplete(fieldConfig);
    }
    callback(fieldConfig);
    return;
  }
  callback(fieldConfig);
};

export const getDynamicSelectOption = (
  value: string,
  options?: ReadonlyArray<IFieldValueExtensionSelect>,
  isSelectAll?: boolean,
  selectAllLabel?: string
) => {
  let newOptions = options?.slice() || [];
  // 当筛选项启用全选时 在数组第一位添加 全选 选项
  if (isSelectAll && selectAllLabel && newOptions.length > 0) {
    newOptions = [
      {
        label: selectAllLabel,
        value: AUTO_TABLE_CUSTOM_VALUE_ALL,
      },
      ...newOptions,
    ];
  }
  if (!options) {
    try {
      newOptions = typeof value === 'string' ? JSON.parse(value) : [];
    } catch (err) {
      // log.error(err);
    }
  }
  return newOptions;
};
// 自定义筛选框搜索
export const searchOptions = (
  value: string,
  options: Array<IFieldValueExtensionSelect>
): Array<IFieldValueExtensionSelect> => {
  const newOptions: Array<IFieldValueExtensionSelect> = [];
  options.forEach((item: IFieldValueExtensionSelect) => {
    if (item.label.toUpperCase().indexOf(value.toUpperCase()) >= 0) {
      newOptions.push(item);
    }
  });
  return newOptions;
};

// 树型下拉框
export const getChildrenKeys = (node: any) => {
  const keys: any = [];
  const children = node.children || [];
  children.forEach((child: any) => {
    keys.push(child.key);
    keys.push(...getChildrenKeys(child));
  });
  return keys;
};

export const getCheckValue = (
  checkedKeys: React.Key[],
  node: any,
  selectAllValue?: React.Key[]
) => {
  let newCheckedKeys = [...checkedKeys];
  // 选中全选
  if (checkedKeys.includes(AUTO_TABLE_CUSTOM_VALUE_ALL) && selectAllValue) {
    newCheckedKeys = selectAllValue;
  } else {
    // 选中父节点时，自动选中其所有子节点
    const childrenKeys = getChildrenKeys(node);
    childrenKeys.forEach((key: any) => {
      if (!newCheckedKeys.includes(key)) {
        newCheckedKeys.push(key);
      }
    });
  }
  return newCheckedKeys;
};

export const getTraverseValue = (checkedKeys: React.Key[], node: any) => {
  let newCheckedKeys = [...checkedKeys];
  // 取消全选
  if (node.key === AUTO_TABLE_CUSTOM_VALUE_ALL) {
    newCheckedKeys = [];
  } else {
    // 取消父节点时，取消其所有子节点的选中状态
    const childrenKeys = getChildrenKeys(node);
    childrenKeys.forEach((key: any) => {
      const index = newCheckedKeys.indexOf(key);
      if (index >= 0) {
        newCheckedKeys.splice(index, 1);
      }
    });
  }
  return newCheckedKeys;
};

// 添加指针-用于排序
export const addPos = (data: any, parentPos = '') => {
  for (let i = 0; i < data.length; i++) {
    const node = data[i];
    const pos = parentPos ? `${parentPos}-${i}` : `${i}`;
    node.pos = pos;
    if (node.children) {
      addPos(node.children, pos);
    }
  }
};

export const getParentIndex = (data: any, key: React.Key) => {
  for (let i = 0; i < data.length; i++) {
    const node = data[i];
    if (node.key === key) {
      const pos = node.pos.split('-');
      return pos.length > 0 ? Number(pos[0]) : Number(node.pos);
    }
    if (node.children) {
      const parentIndex: number = getParentIndex(node.children, key);
      if (parentIndex > -1) {
        return parentIndex;
      }
    }
  }
  return -1;
};
